import {
    ChromeFilled,
    CompassOutlined,
    DownloadOutlined,
    EllipsisOutlined,
    GlobalOutlined,
    PlusSquareOutlined,
    ShareAltOutlined,
} from "@ant-design/icons";
import { Flex } from "antd";
import { FC, ReactNode } from "react";
import { type Browser, detectBrowser } from "tm-shared/utils";

import BaseButton from "@/shared/ui/BaseButton";
import { BaseModal } from "@/shared/ui/BaseModal";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";

import { StyledInfoCircleOutlined, StyledWarningContainer } from "./Styled";

interface InstallWarningProps {
    isOpen: boolean;
    onClose: () => void;
    onInstall: () => void;
    canInstall: boolean;
}

const browserIconsMap: Record<Browser, ReactNode> = {
    chrome: <ChromeFilled />,
    safari: <CompassOutlined />,
    other: <GlobalOutlined />,
};

const browserNameMap: Record<Browser, string> = {
    chrome: "Google Chrome",
    safari: "Safari",
    other: "основному браузері",
};

const getSafariSteps = () => {
    return [
        {
            key: 1,
            icon: browserIconsMap["safari"],
            label: "Відкрити застосунок у Safari",
        },
        {
            key: 2,
            icon: <EllipsisOutlined />,
            label: "Натисніть «Більше», якщо немає значка «Поділитися»",
        },
        {
            key: 3,
            icon: <ShareAltOutlined />,
            label: "Натиснути «Поділитися»",
        },
        {
            key: 4,
            icon: <PlusSquareOutlined />,
            label: " Прокрутіть вниз до «На Початковий екран»",
        },
    ];
};

const getCommonSteps = (browser: Browser) => {
    return [
        {
            key: 1,
            icon: browserIconsMap[browser],
            label: `Відкрити застосунок у ${browserNameMap[browser]}`,
        },
        {
            key: 2,
            icon: <EllipsisOutlined />,
            label: "Натисніть меню «⋮» (або «⋯»)",
        },
        {
            key: 4,
            icon: <DownloadOutlined />,
            label: "Прокрутіть вниз до «Додати на Головний екран»",
        },
    ];
};

const InstallWarning: FC<InstallWarningProps> = ({
    isOpen,
    onClose,
    onInstall,
    canInstall,
}) => {
    const browser = detectBrowser();

    const steps =
        browser === "safari" ? getSafariSteps() : getCommonSteps(browser);

    return (
        <BaseModal
            title="Застосунок відкрито у браузері"
            open={isOpen}
            onClose={onClose}
            centered
            actions={null}
            mask={{ closable: true }}
        >
            <Flex vertical gap={16}>
                {browser === "other" && (
                    <StyledWarningContainer>
                        <StyledInfoCircleOutlined />

                        <PrimaryText textAlign="center">
                            Для коректної роботи та встановлення додатку ми
                            рекомендуємо використовувати Google Chrome або
                            Safari. У цьому браузері можливі обмеження або
                            некоректна робота застосунку
                        </PrimaryText>
                    </StyledWarningContainer>
                )}

                <PrimaryText textAlign="center">
                    Нормальна робота в Trade Master доступна тільки у
                    встановленому додатку.
                </PrimaryText>

                {!canInstall && (
                    <Flex vertical gap={12}>
                        {steps.map(({ key, label, icon }) => (
                            <Flex key={key} gap={12} align="center">
                                <PrimaryText fontSize={24}>{icon}</PrimaryText>

                                <SecondaryText>{label}</SecondaryText>
                            </Flex>
                        ))}
                    </Flex>
                )}

                <Flex vertical gap={8}>
                    {canInstall && (
                        <BaseButton type="primary" onClick={onInstall}>
                            Встановити
                        </BaseButton>
                    )}

                    <BaseButton type="default" onClick={onClose}>
                        Повернутися назад
                    </BaseButton>
                </Flex>
            </Flex>
        </BaseModal>
    );
};

export default InstallWarning;
